import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { CardSkeleton } from "@/components/loading-skeletons";
import { supabase } from "@/integrations/supabase/client";
import { PageHeader } from "@/components/page-header";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/store/settings")({
  head: () => ({ meta: [{ title: "Pengaturan Store — MI Al-Munajah Admin" }] }),
  component: SettingsPage,
});

type Settings = {
  id?: string;
  whatsapp_number: string;
  operating_hours: string;
  address: string;
  shipping_info: string;
  purchase_notes: string;
  footer_text: string;
};

const empty: Settings = {
  whatsapp_number: "", operating_hours: "", address: "",
  shipping_info: "", purchase_notes: "", footer_text: "",
};

function SettingsPage() {
  const [form, setForm] = useState<Settings>(empty);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from("store_settings").select("*").order("created_at").limit(1).maybeSingle();
      if (data) {
        setForm({
          id: data.id,
          whatsapp_number: data.whatsapp_number ?? "",
          operating_hours: data.operating_hours ?? "",
          address: data.address ?? "",
          shipping_info: data.shipping_info ?? "",
          purchase_notes: data.purchase_notes ?? "",
          footer_text: data.footer_text ?? "",
        });
      }
      setLoading(false);
    })();
  }, []);

  const save = async () => {
    setBusy(true);
    const payload = {
      whatsapp_number: form.whatsapp_number.trim() || null,
      operating_hours: form.operating_hours.trim() || null,
      address: form.address.trim() || null,
      shipping_info: form.shipping_info.trim() || null,
      purchase_notes: form.purchase_notes.trim() || null,
      footer_text: form.footer_text.trim() || null,
    };
    const { error } = form.id
      ? await supabase.from("store_settings").update(payload).eq("id", form.id)
      : await supabase.from("store_settings").insert(payload);
    setBusy(false);
    if (error) return toast.error(error.message);
    toast.success("Pengaturan disimpan");
  };

  if (loading) return <CardSkeleton lines={5} />;

  return (
    <div>
      <PageHeader title="Pengaturan Store" description="Informasi toko yang ditampilkan di aplikasi Android." />
      <Card className="p-6 rounded-2xl max-w-3xl space-y-4">
        <div>
          <Label>Nomor WhatsApp</Label>
          <Input value={form.whatsapp_number} onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })} placeholder="628xxxxxxxxxx" />
        </div>
        <div>
          <Label>Jam Operasional</Label>
          <Input value={form.operating_hours} onChange={(e) => setForm({ ...form, operating_hours: e.target.value })} placeholder="Senin–Jumat 07.00–15.00" />
        </div>
        <div>
          <Label>Alamat</Label>
          <Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
        </div>
        <div>
          <Label>Informasi Pengiriman</Label>
          <Textarea rows={3} value={form.shipping_info} onChange={(e) => setForm({ ...form, shipping_info: e.target.value })} />
        </div>
        <div>
          <Label>Catatan Pembelian</Label>
          <Textarea rows={3} value={form.purchase_notes} onChange={(e) => setForm({ ...form, purchase_notes: e.target.value })} />
        </div>
        <div>
          <Label>Text Footer</Label>
          <Textarea rows={2} value={form.footer_text} onChange={(e) => setForm({ ...form, footer_text: e.target.value })} />
        </div>
        <div className="flex justify-end">
          <Button onClick={save} disabled={busy}>Simpan</Button>
        </div>
      </Card>
    </div>
  );
}
